"use client";

import { usePathname, useRouter } from "next/navigation";
import type * as React from "react";
import { useEffect, useState } from "react";
import Footer from "@/app/footer";
import Header from "@/app/header";
import { VisitorActivityPopups } from "@/components/visitor_activity_popups";
import { UserAuthProvider } from "@/contexts/user_auth_context";

interface PublicLayoutWrapperProps {
	children: React.ReactNode;
}

const PUBLIC_PRELOAD_ROUTES = ["/"] as const;

function scheduleIdleWork(callback: () => void) {
	if ("requestIdleCallback" in window) {
		const idleId = window.requestIdleCallback(callback, { timeout: 2500 });
		return () => window.cancelIdleCallback(idleId);
	}

	const timeoutId = globalThis.setTimeout(callback, 1800);
	return () => globalThis.clearTimeout(timeoutId);
}

export function PublicRoutePreloader() {
	const router = useRouter();

	useEffect(() => {
		return scheduleIdleWork(() => {
			for (const route of PUBLIC_PRELOAD_ROUTES) {
				router.prefetch(route);
			}
		});
	}, [router]);

	return null;
}

function useIdleEngagementWidgets() {
	const [enabled, setEnabled] = useState(false);

	useEffect(() => scheduleIdleWork(() => setEnabled(true)), []);

	return enabled;
}

/**
 * Wrapper component that conditionally renders the public Header and Footer.
 * Admin routes (/admin/*) do not show the public navigation.
 */
export function PublicLayoutWrapper({ children }: PublicLayoutWrapperProps) {
	const pathname = usePathname();
	const isAdminRoute = pathname.startsWith("/admin");
	const isAuthRoute = pathname.includes("/auth/");
	const isDashboardRoute = pathname.includes("/dashboard");
	const isHomepage = pathname === "/";
	const isDesignLab = pathname.startsWith("/design-lab");
	const shouldLoadEngagementWidgets = useIdleEngagementWidgets();

	if (isAdminRoute || isAuthRoute || isDashboardRoute || isDesignLab) {
		// These pages have their own layout - render children without public header/footer
		return <>{children}</>;
	}

	// Public pages get the standard header and footer wrapped with UserAuthProvider
	return (
		<UserAuthProvider>
			<PublicRoutePreloader />
			<Header />
			<main className={`min-h-[74vh] ${isHomepage ? "pt-0" : "pt-20"}`}>
				{children}
			</main>
			{shouldLoadEngagementWidgets && <VisitorActivityPopups />}
			<Footer />
		</UserAuthProvider>
	);
}
